import React, { createContext, useContext, useState, useEffect } from 'react';

const SaleContext = createContext();

export const useSale = () => {
  const context = useContext(SaleContext);
  if (!context) {
    throw new Error('useSale must be used within a SaleProvider');
  }
  return context;
};

export const SaleProvider = ({ children }) => {
  const [saleConfig, setSaleConfig] = useState({
    is_active: false,
    discount_percent: 20,
    sale_title: "Limited Time Offer",
    sale_subtitle: "Book now and save!",
    remaining_slots: 5
  });
  const [loading, setLoading] = useState(true);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchSaleStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/sale-status`);
      if (response.ok) {
        const data = await response.json();
        setSaleConfig(data);
        // Reset dismissed state when sale status changes
        if (!data.is_active) {
          setBannerDismissed(false);
        }
      }
    } catch (error) {
      console.error('Error fetching sale status:', error);
    } finally {
      setLoading(false);
    }
  };

  const decrementSlot = async () => {
    // Generate or get visitor ID from localStorage
    let visitorId = localStorage.getItem('prowash_visitor_id');
    if (!visitorId) {
      visitorId = 'visitor_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('prowash_visitor_id', visitorId);
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/decrement-slot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor_id: visitorId })
      });
      if (response.ok) {
        const data = await response.json();
        setSaleConfig(prev => ({ ...prev, remaining_slots: data.remaining_slots }));
        return data;
      }
    } catch (error) {
      console.error('Error decrementing slot:', error);
    }
    return null;
  };

  useEffect(() => {
    fetchSaleStatus();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchSaleStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const calculateSalePrice = (originalPrice) => {
    if (!saleConfig.is_active) return originalPrice;
    const discount = (originalPrice * saleConfig.discount_percent) / 100;
    return Math.round(originalPrice - discount);
  };

  const dismissBanner = () => {
    setBannerDismissed(true);
  };

  // Banner is visible only if sale is active AND not dismissed
  const isBannerVisible = saleConfig.is_active && !bannerDismissed;

  const value = {
    saleConfig,
    loading,
    calculateSalePrice,
    refreshSaleStatus: fetchSaleStatus,
    bannerDismissed,
    dismissBanner,
    isBannerVisible,
    decrementSlot
  };

  return (
    <SaleContext.Provider value={value}>
      {children}
    </SaleContext.Provider>
  );
};

export default SaleContext;
